import eventStreamService from './eventStreamService';

const NOISE_EVENT_PREFIXES = ['llm_request_'];
const NON_LEARNING_EVENT_SUFFIXES = ['_requested', '_started'];

function normalizeEventType(event) {
  return String(event?.event_type || event?.event_name || '')
    .trim()
    .toLowerCase();
}

function isNoiseEvent(event) {
  const eventType = normalizeEventType(event);

  if (!eventType) return false;
  if (NOISE_EVENT_PREFIXES.some((prefix) => eventType.startsWith(prefix))) return true;
  if (event.event_family === 'system') return true;

  return false;
}

function isLearningMomentEvent(event) {
  const eventType = normalizeEventType(event);
  if (!eventType || isNoiseEvent(event)) return false;
  if (NON_LEARNING_EVENT_SUFFIXES.some((suffix) => eventType.endsWith(suffix))) return false;
  return true;
}

function buildEventIdentity(event) {
  const scoreKey = [
    Number.isFinite(Number(event?.score_pct)) ? Number(event.score_pct).toFixed(2) : null,
    Number.isFinite(Number(event?.score_raw)) ? Number(event.score_raw) : null,
    Number.isFinite(Number(event?.score_max)) ? Number(event.score_max) : null,
  ]
    .filter((value) => value != null)
    .join(':');

  return [
    event?.stream_key,
    event?.session_id,
    event?.correlation_id,
    event?.conversation_key,
    event?.exam_id,
    event?.task_id,
    event?.entity_id,
    scoreKey,
    toDateKey(event?.occurred_at),
  ]
    .filter(Boolean)
    .map((value) => String(value))
    .join('::');
}

function buildCanonicalAnalyticsEvents(events) {
  const learningEvents = (Array.isArray(events) ? events : []).filter(isLearningMomentEvent);
  const examAttempts = new Map();
  const taskCompletions = new Map();
  const canonicalEvents = [];

  learningEvents.forEach((event) => {
    const eventType = normalizeEventType(event);

    if (
      event.event_family === 'exam' &&
      ['exam_submission_succeeded', 'exam_score_recorded'].includes(eventType)
    ) {
      const identity = buildEventIdentity(event);
      const existing = examAttempts.get(identity);
      const existingType = normalizeEventType(existing);
      const shouldPreferSubmission =
        eventType === 'exam_submission_succeeded' && existingType !== 'exam_submission_succeeded';
      const shouldReplaceWithNewerSamePriority =
        new Date(event.occurred_at) > new Date(existing?.occurred_at);

      if (
        !existing ||
        shouldPreferSubmission ||
        (eventType === existingType && shouldReplaceWithNewerSamePriority) ||
        (!existingType && shouldReplaceWithNewerSamePriority)
      ) {
        examAttempts.set(identity, event);
      }
      return;
    }

    if (
      event.event_family === 'task' &&
      ['task_completed', 'task_finished', 'task_submitted'].includes(eventType)
    ) {
      const taskIdentity = [
        event.task_id || event.entity_id || 'task',
        event.session_id || event.correlation_id || toDateKey(event.occurred_at),
      ]
        .filter(Boolean)
        .join('::');
      const existing = taskCompletions.get(taskIdentity);
      if (!existing || new Date(event.occurred_at) > new Date(existing.occurred_at)) {
        taskCompletions.set(taskIdentity, event);
      }
      return;
    }

    canonicalEvents.push(event);
  });

  return canonicalEvents
    .concat(Array.from(examAttempts.values()))
    .concat(Array.from(taskCompletions.values()));
}

function buildFailureEvents(events) {
  return (Array.isArray(events) ? events : []).filter(
    (event) => event?.success === false && isLearningMomentEvent(event)
  );
}

function toDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

function normalizeScore(event) {
  const rawScorePct = Number(event?.score_pct);
  if (Number.isFinite(rawScorePct)) return rawScorePct;

  const raw = Number(event?.score_raw);
  const max = Number(event?.score_max);
  if (Number.isFinite(raw) && Number.isFinite(max) && max > 0) {
    return Number(((raw / max) * 100).toFixed(2));
  }

  return null;
}

function getCompletedExamSubmissions(events) {
  return (Array.isArray(events) ? events : []).filter((event) => {
    if (event?.event_family !== 'exam') return false;
    if (event?.event_type !== 'exam_submission_succeeded') return false;
    return Number.isFinite(normalizeScore(event));
  });
}

function resolveDurationMs(event) {
  const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};
  const directDurationMs = Number(event?.duration_ms);
  if (Number.isFinite(directDurationMs) && directDurationMs > 0) {
    return directDurationMs;
  }

  const payloadDurationMs = Number(payload.durationMs ?? payload.duration_ms);
  if (Number.isFinite(payloadDurationMs) && payloadDurationMs > 0) {
    return payloadDurationMs;
  }

  const payloadTimeSpentSeconds = Number(payload.timeSpent ?? payload.time_spent);
  if (Number.isFinite(payloadTimeSpentSeconds) && payloadTimeSpentSeconds > 0) {
    return payloadTimeSpentSeconds * 1000;
  }

  return 0;
}

function resolveCourseId(event) {
  const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};
  return event?.course_id || payload.courseId || payload.course_id || null;
}

function resolveCourseTitle(event, courseTitleMap) {
  const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};
  const resolvedCourseId = resolveCourseId(event);

  if (resolvedCourseId && courseTitleMap.get(String(resolvedCourseId))) {
    return courseTitleMap.get(String(resolvedCourseId));
  }

  return payload.courseTitle || payload.course_title || null;
}

function buildExamContextKeys(event) {
  return [event?.session_id, event?.exam_id, event?.entity_id]
    .filter((value) => value != null && value !== '')
    .map((value) => String(value));
}

function buildExamCourseLookup(events, courses) {
  const { courseTitleMap } = buildCourseContextLookup(courses);
  const lookup = new Map();

  (Array.isArray(events) ? events : []).forEach((event) => {
    if (event?.event_family !== 'exam') return;

    const courseId = resolveCourseId(event);
    const courseTitle = resolveCourseTitle(event, courseTitleMap);
    if (!courseId && !courseTitle) return;

    buildExamContextKeys(event).forEach((key) => {
      const current = lookup.get(key) || {};
      lookup.set(key, {
        courseId: current.courseId || courseId || null,
        courseTitle: current.courseTitle || courseTitle || null,
      });
    });
  });

  return lookup;
}

function reconcileExamEventCourseContext(event, examCourseLookup) {
  if (!event || event.event_family !== 'exam') return event;
  if (resolveCourseId(event) && resolveCourseTitle(event, new Map())) return event;

  const contextMatch = buildExamContextKeys(event)
    .map((key) => examCourseLookup.get(key))
    .find((value) => value?.courseId || value?.courseTitle);

  if (!contextMatch) return event;

  const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};

  return {
    ...event,
    course_id: event.course_id || contextMatch.courseId || null,
    payload: {
      ...payload,
      courseId: payload.courseId || payload.course_id || contextMatch.courseId || null,
      courseTitle: payload.courseTitle || payload.course_title || contextMatch.courseTitle || null,
    },
  };
}

function collectCourseQuizStats(events, courses) {
  const { courseTitleMap } = buildCourseContextLookup(courses);
  const courseMap = new Map();

  getCompletedExamSubmissions(events).forEach((event) => {
    const resolvedCourseId = resolveCourseId(event);
    const courseId = resolvedCourseId ? String(resolvedCourseId) : 'unknown_course';
    const scorePct = normalizeScore(event);
    if (!Number.isFinite(scorePct)) return;

    const current = courseMap.get(courseId) || {
      courseId,
      title: resolveCourseTitle(event, courseTitleMap) || courseId,
      attempts: 0,
      scores: [],
      latestAt: null,
    };

    current.attempts += 1;
    current.scores.push(scorePct);
    current.latestAt = event.occurred_at;
    courseMap.set(courseId, current);
  });

  return Array.from(courseMap.values()).map((item) => ({
    courseId: item.courseId,
    title: item.title,
    attempts: item.attempts,
    avgScorePct: Number(average(item.scores).toFixed(1)),
    bestScorePct: Number(Math.max(...item.scores).toFixed(1)),
    latestScorePct: Number(item.scores[item.scores.length - 1].toFixed(1)),
    latestAt: item.latestAt,
  }));
}

function sum(values) {
  return values.reduce((total, value) => total + value, 0);
}

function average(values) {
  if (!values.length) return null;
  return sum(values) / values.length;
}

function buildFamilyBreakdown(events) {
  const byFamily = new Map();

  events.forEach((event) => {
    const family = event.event_family || 'system';
    const current = byFamily.get(family) || { family, count: 0, failures: 0 };
    current.count += 1;
    if (event.success === false) current.failures += 1;
    byFamily.set(family, current);
  });

  return Array.from(byFamily.values()).sort((left, right) => right.count - left.count);
}

function buildDailySeries(events, days) {
  const seriesMap = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    const key = date.toISOString().slice(0, 10);
    seriesMap.set(key, { date: key, events: 0, exams: 0, chats: 0, scenes: 0 });
  }

  events.forEach((event) => {
    const key = toDateKey(event.occurred_at);
    const bucket = key ? seriesMap.get(key) : null;
    if (!bucket) return;
    bucket.events += 1;
    if (event.event_family === 'exam') bucket.exams += 1;
    if (event.event_family === 'chat') bucket.chats += 1;
    if (['scene', 'visual', 'text'].includes(event.event_family)) bucket.scenes += 1;
  });

  return Array.from(seriesMap.values());
}

function buildHeatmap(events) {
  const matrix = Array.from({ length: 7 }, (_, day) =>
    Array.from({ length: 24 }, (_, hour) => ({
      day,
      hour,
      count: 0,
    }))
  );

  events.forEach((event) => {
    const date = new Date(event.occurred_at);
    if (Number.isNaN(date.getTime())) return;
    const day = (date.getDay() + 6) % 7;
    const hour = date.getHours();
    matrix[day][hour].count += 1;
  });

  return matrix;
}

function buildActivityStreak(events) {
  const activeDateSet = new Set(
    events.map((event) => toDateKey(event.occurred_at)).filter(Boolean)
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let currentStreak = 0;
  for (let cursor = new Date(today); ; cursor.setDate(cursor.getDate() - 1)) {
    const key = cursor.toISOString().slice(0, 10);
    if (!activeDateSet.has(key)) break;
    currentStreak += 1;
  }

  const activeDates = Array.from(activeDateSet).sort();
  let bestStreak = 0;
  let rolling = 0;
  let previous = null;

  activeDates.forEach((key) => {
    const date = new Date(`${key}T00:00:00`);
    if (previous) {
      const diffDays = Math.round((date - previous) / 86400000);
      rolling = diffDays === 1 ? rolling + 1 : 1;
    } else {
      rolling = 1;
    }
    bestStreak = Math.max(bestStreak, rolling);
    previous = date;
  });

  return {
    currentStreak,
    bestStreak,
    activeDateSet,
  };
}

function buildStreakCalendar(activeDateSet, weeks = 18) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  start.setDate(today.getDate() - weeks * 7 + 1);

  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);

  const cells = [];
  const cursor = new Date(start);
  for (let index = 0; index < weeks * 7; index += 1) {
    const key = cursor.toISOString().slice(0, 10);
    cells.push({
      date: key,
      count: activeDateSet.has(key) ? 1 : 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  const columns = [];
  for (let index = 0; index < cells.length; index += 7) {
    columns.push(cells.slice(index, index + 7));
  }

  return columns;
}

function buildPageBreakdown(events) {
  const pageMap = new Map();

  events.forEach((event) => {
    const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};
    const page = payload.page || payload.route || event.origin || 'unknown';
    const current = pageMap.get(page) || {
      page,
      count: 0,
      failures: 0,
      avgDurationMs: 0,
      durationSamples: [],
    };

    current.count += 1;
    if (event.success === false) current.failures += 1;
    const durationMs = resolveDurationMs(event);
    if (durationMs > 0) {
      current.durationSamples.push(durationMs);
    }
    pageMap.set(page, current);
  });

  return Array.from(pageMap.values())
    .map((page) => ({
      page: page.page,
      count: page.count,
      failures: page.failures,
      avgDurationMs: page.durationSamples.length ? average(page.durationSamples) : null,
    }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 8);
}

function buildExamHistory(events) {
  const examEvents = getCompletedExamSubmissions(events)
    .map((event) => ({
      occurredAt: event.occurred_at,
      examId: event.exam_id || event.entity_id || 'unknown_exam',
      courseId: resolveCourseId(event),
      unitId: event.unit_id || null,
      scorePct: normalizeScore(event),
      scoreRaw: event.score_raw,
      scoreMax: event.score_max,
    }))
    .filter((event) => Number.isFinite(event.scorePct))
    .sort((left, right) => new Date(left.occurredAt) - new Date(right.occurredAt));

  const latest = examEvents.slice(-8).reverse();
  const scores = examEvents.map((event) => event.scorePct);

  return {
    events: latest,
    averageScorePct: scores.length ? Number(average(scores).toFixed(1)) : null,
    bestScorePct: scores.length ? Number(Math.max(...scores).toFixed(1)) : null,
    latestScorePct: scores.length ? Number(scores[scores.length - 1].toFixed(1)) : null,
    totalAttempts: examEvents.length,
  };
}

function buildExamPerformanceCards(events, courses) {
  return collectCourseQuizStats(events, courses)
    .sort((left, right) => new Date(right.latestAt) - new Date(left.latestAt))
    .slice(0, 6);
}

function buildCourseActivity(events, courses) {
  const courseTitleMap = new Map(
    (Array.isArray(courses) ? courses : []).map((course) => [
      String(course.id),
      course.title || course.id,
    ])
  );
  const courseMap = new Map();

  events.forEach((event) => {
    const resolvedCourseId = resolveCourseId(event);
    if (!resolvedCourseId) return;
    const key = String(resolvedCourseId);
    const current = courseMap.get(key) || {
      courseId: key,
      title: resolveCourseTitle(event, courseTitleMap) || key,
      events: 0,
      exams: 0,
      chats: 0,
      tasks: 0,
    };

    current.events += 1;
    if (event.event_family === 'exam') current.exams += 1;
    if (event.event_family === 'chat') current.chats += 1;
    if (event.event_family === 'task') current.tasks += 1;
    courseMap.set(key, current);
  });

  return Array.from(courseMap.values())
    .sort((left, right) => right.events - left.events)
    .slice(0, 6);
}

function buildCourseContextLookup(courses) {
  const courseTitleMap = new Map();
  const unitTitleMap = new Map();

  (Array.isArray(courses) ? courses : []).forEach((course) => {
    const courseId = course?.id != null ? String(course.id) : null;
    if (courseId) {
      courseTitleMap.set(courseId, course.title || courseId);
    }

    (Array.isArray(course?.units) ? course.units : []).forEach((unit) => {
      const unitId = unit?.id != null ? String(unit.id) : null;
      if (unitId) {
        unitTitleMap.set(unitId, unit.title || unit.unit_title || unit.lessonTitle || unitId);
      }
    });
  });

  return { courseTitleMap, unitTitleMap };
}

function isInternalTopicCandidate(value) {
  const text = String(value || '').trim();
  if (!text) return true;

  const normalized = text.toLowerCase();
  return (
    normalized.startsWith('/') ||
    normalized.includes('generate-') ||
    normalized.includes('/generate') ||
    normalized.includes('professor-chat') ||
    normalized === 'professor' ||
    normalized === 'scene' ||
    normalized === 'chat'
  );
}

function deriveTopicLabel(event, courseContext) {
  const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};

  const explicitCandidates = [
    payload.topic,
    payload.concept,
    payload.unitTitle,
    payload.lessonTitle,
    event.lesson_id,
    event.entity_id,
  ].filter((value) => value != null && value !== '');

  const explicitTopic = explicitCandidates.find((value) => !isInternalTopicCandidate(value));
  if (explicitTopic) return String(explicitTopic).trim();

  const resolvedCourseId = resolveCourseId(event);
  const courseTitle =
    (resolvedCourseId && courseContext.courseTitleMap.get(String(resolvedCourseId))) ||
    resolveCourseTitle(event, courseContext.courseTitleMap) ||
    null;
  const unitTitle =
    (event.unit_id && courseContext.unitTitleMap.get(String(event.unit_id))) || null;

  if (courseTitle && unitTitle) return `${courseTitle} · ${unitTitle}`;
  if (unitTitle) return unitTitle;
  if (courseTitle) return courseTitle;

  return null;
}

function buildFinishedTasks(events) {
  const taskMap = new Map();

  events
    .filter(
      (event) =>
        event.event_family === 'task' &&
        ['task_completed', 'task_finished', 'task_submitted'].includes(event.event_type)
    )
    .forEach((event) => {
      const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};
      const taskId =
        event.task_id || event.entity_id || payload.taskId || payload.taskTitle || 'task';
      const title = payload.taskTitle || payload.taskName || taskId;
      const current = taskMap.get(taskId) || {
        taskId,
        title,
        completions: 0,
        latestAt: null,
      };

      current.completions += 1;
      current.latestAt = event.occurred_at;
      taskMap.set(taskId, current);
    });

  return Array.from(taskMap.values())
    .sort((left, right) => right.completions - left.completions)
    .slice(0, 6);
}

function buildTopicSignals(events, courses) {
  const courseContext = buildCourseContextLookup(courses);
  const topicMap = new Map();

  events.forEach((event) => {
    const topic = deriveTopicLabel(event, courseContext);
    if (!topic) return;

    const current = topicMap.get(topic) || {
      topic,
      count: 0,
      chats: 0,
      exams: 0,
      scenes: 0,
    };

    current.count += 1;
    if (event.event_family === 'chat') current.chats += 1;
    if (event.event_family === 'exam') current.exams += 1;
    if (['scene', 'visual', 'text'].includes(event.event_family)) current.scenes += 1;
    topicMap.set(topic, current);
  });

  return Array.from(topicMap.values())
    .sort((left, right) => right.count - left.count)
    .slice(0, 6);
}

function buildSummary(events, courses) {
  const examScores = getCompletedExamSubmissions(events)
    .map((event) => normalizeScore(event))
    .filter((score) => Number.isFinite(score));
  const avgExamScore = examScores.length ? Number(average(examScores).toFixed(1)) : null;
  const activeDays = new Set(events.map((event) => toDateKey(event.occurred_at)).filter(Boolean))
    .size;
  const failedEvents = events.filter((event) => event.success === false).length;
  const activeCourseCount = new Set(events.map((event) => resolveCourseId(event)).filter(Boolean))
    .size;
  const studyMinutes = Math.round(sum(events.map((event) => resolveDurationMs(event))) / 60000);

  return {
    totalEvents: events.length,
    activeDays,
    studyMinutes,
    avgExamScore,
    failedEvents,
    courseCount: Array.isArray(courses) ? courses.length : 0,
    activeCourseCount,
    quizCourseCount: collectCourseQuizStats(events, courses).length,
    quizAttemptCount: examScores.length,
  };
}

function buildRecentTimeline(events) {
  return events
    .filter((event) => {
      if (isNoiseEvent(event)) return false;
      return isLearningMomentEvent(event) || event.success === false;
    })
    .slice()
    .sort((left, right) => new Date(right.occurred_at) - new Date(left.occurred_at))
    .slice(0, 12)
    .map((event) => ({
      id: event.id,
      occurredAt: event.occurred_at,
      family: event.event_family,
      type: event.event_type,
      success: event.success !== false,
      page: event.payload?.page || event.payload?.route || event.origin || null,
      courseId: resolveCourseId(event),
      unitId: event.unit_id,
      examId: event.exam_id,
      sceneId: event.scene_id,
      scorePct:
        event.event_family === 'exam' &&
        ['exam_submission_succeeded', 'exam_score_recorded'].includes(event.event_type)
          ? normalizeScore(event)
          : null,
    }));
}

export async function fetchUserAnalyticsSnapshot({ userId, days = 30, courses = [] }) {
  const emptyStreak = buildActivityStreak([]);

  if (!userId) {
    return {
      data: {
        summary: buildSummary([], courses),
        streak: {
          currentStreak: emptyStreak.currentStreak,
          bestStreak: emptyStreak.bestStreak,
          calendar: buildStreakCalendar(emptyStreak.activeDateSet),
        },
        mostActiveCourses: [],
        finishedTasks: [],
        examPerformanceCards: [],
        familyBreakdown: [],
        dailySeries: buildDailySeries([], days),
        heatmap: buildHeatmap([]),
        pageBreakdown: [],
        examHistory: buildExamHistory([]),
        topicSignals: [],
        recentTimeline: [],
      },
      error: null,
    };
  }

  const from = new Date();
  from.setDate(from.getDate() - Math.max(1, Number(days) || 30));

  const { data: events, error } = await eventStreamService.fetchEvents({
    userId,
    from: from.toISOString(),
    limit: 1500,
  });

  const safeEvents = Array.isArray(events) ? events : [];
  const examCourseLookup = buildExamCourseLookup(safeEvents, courses);
  const enrichedEvents = safeEvents.map((event) =>
    reconcileExamEventCourseContext(event, examCourseLookup)
  );
  const canonicalEvents = buildCanonicalAnalyticsEvents(enrichedEvents);
  const successfulCanonicalEvents = canonicalEvents.filter((event) => event.success !== false);
  const failureEvents = buildFailureEvents(enrichedEvents);
  const streak = buildActivityStreak(successfulCanonicalEvents);

  return {
    data: {
      summary: buildSummary(successfulCanonicalEvents.concat(failureEvents), courses),
      streak: {
        currentStreak: streak.currentStreak,
        bestStreak: streak.bestStreak,
        calendar: buildStreakCalendar(streak.activeDateSet),
      },
      mostActiveCourses: buildCourseActivity(successfulCanonicalEvents, courses),
      finishedTasks: buildFinishedTasks(successfulCanonicalEvents),
      examPerformanceCards: buildExamPerformanceCards(successfulCanonicalEvents, courses),
      familyBreakdown: buildFamilyBreakdown(successfulCanonicalEvents.concat(failureEvents)),
      dailySeries: buildDailySeries(successfulCanonicalEvents, days),
      heatmap: buildHeatmap(successfulCanonicalEvents),
      pageBreakdown: buildPageBreakdown(successfulCanonicalEvents.concat(failureEvents)),
      examHistory: buildExamHistory(successfulCanonicalEvents),
      topicSignals: buildTopicSignals(successfulCanonicalEvents, courses),
      recentTimeline: buildRecentTimeline(successfulCanonicalEvents.concat(failureEvents)),
    },
    error,
  };
}

export default {
  fetchUserAnalyticsSnapshot,
};
